import styled from 'styled-components';
import Endash from '../components/basics/Endash';
import CheckIcon from '../components/icons/CheckIcon';
import WarningIcon from '../components/icons/WarningIcon';
import { IconSpan } from '../components/left-menu/menu-components';
import dayjs from './dayjs';
import { ANNUAL_MULTI_YEAR, MULTI_YEAR } from '../constants/years';

// Note: this may show negative zero, eg. ($0) if the number was close to zero
// and negative.
export const asCurrencyWithDigits = (
  minimumFractionDigits,
  maximumFractionDigits = minimumFractionDigits,
) => (number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencySign: 'accounting',
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(number);

export const asCurrency = (number, { showCents = false } = {}) => {
  const digits = showCents ? 2 : 0;
  return asCurrencyWithDigits(digits)(number).replace('.00', ''); // Never show .00
};

export const asCurrencyWithCents = (number) =>
  asCurrency(number, { showCents: true });

// Returns asCurrencyWithCents if the value is a number, otherwise returns the
// original value.
export const numberAsCurrencyWithCents = (v) =>
  typeof v === 'number' ? asCurrencyWithCents(v) : v;

export const asCurrencyFromCents = (amount) =>
  asCurrency(amount / 100, { showCents: true });

export const asCostCurrency = (number, opts) => {
  return asCurrency(0 - number, opts);
};

export const asHours = (number) => (Math.ceil(number * 10) / 10.0).toFixed(1);

export const asDecimal = (number, { digits = 2 } = {}) =>
  number.toFixed(digits);

// digits is the number of digits to round to (i.e. the maximum number of digits
// to keep)
// minDigits is the minimum number of digits to keep. E.g. if 1.1 should be
// displayed as '1.10' (like currency), use minDigits of 2
// If all of the numbers after the decimal point are zeros, they will be removed
// (e.g. 1.00 -> '1')
// See tests for more examples
export const asRoundedDecimal = (
  number,
  { digits = 2, minDigits = digits } = {},
) =>
  asDecimal(number, { digits })
    // Remove any trailing zeros, preserving at least minDigits digits
    .replace(new RegExp(`(\\.\\d{${minDigits}}\\d*?[1-9]?)0+$`), '$1')
    // If there are only zeros left, remove them
    .replace(/\.0*$/, '');

// Returns asRoundedDecimal if the value is a number, otherwise returns the
// original value.
export const numberAsRoundedDecimal = (v, opts) =>
  typeof v === 'number' ? asRoundedDecimal(v, opts) : v;

export const asQuantity = (number = 0, { unit, singular, digits = 0 } = {}) => {
  const value = number.toLocaleString('en-US', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
  const displayUnit = value === '1' && singular ? singular : unit;
  // Return the comma formatted number, like "20,430"
  return `${value}${displayUnit ? ` ${displayUnit}` : ''}`;
};

// Show two decimal places for nonzero numbers under 1000. Otherwise,
// show comma formatted integer.
export const asDynamicDecimal = (number, opts) =>
  number === 0 || number >= 1000
    ? asQuantity(number, opts)
    : asDecimal(number, opts);

// Show two decimal places for nonzero numbers under 1000, truncating zeros.
// Otherwise, show comma formatted integer, or original value if not a number.
export const numberAsDynamicRoundedDecimal = (v, opts) =>
  typeof v === 'number'
    ? v === 0 || v >= 1000
      ? asQuantity(v, opts)
      : asRoundedDecimal(v, opts)
    : v;

export const asDynamicDecimalWithUnit = (unit) => (number) =>
  `${asDynamicDecimal(number)}${unit ? ` ${unit}` : ''}`;

export const unitForValue = (plural, singular) => (value) =>
  value === 1 ? singular : plural;

// A curried version of asQuantity that returns a format function for a given unit.
export const asQuantityWithUnit = (unit, singular) => (number) =>
  asQuantity(number, { unit, singular });

export const asQuantityOrNa = (number, { unit } = {}) =>
  number === 0 || number == null ? 'N/A' : asQuantity(number, { unit });

export const asUnitPrice = (number, { unit } = {}) =>
  `${asCurrency(number, { showCents: true })} / ${unit || 'unit'}`;

export const asPercentage = (number, opts) => {
  if (number == null) return null;
  if (typeof number !== 'number') return number;
  const value = asDecimal(number, opts);
  const absValue = Math.abs(value);
  return value < 0 ? `(${absValue}%)` : `${absValue}%`;
};

export const asPercentageOrBlank = (number) =>
  number === 0 || number === null ? '-' : asPercentage(number);

export const asPercentageOrNa = (number) =>
  number === undefined ? (
    <Endash />
  ) : number === 0 || number === null ? (
    'N/A'
  ) : (
    asPercentage(number)
  );

export const asLengthWithList = (list) =>
  list == null ? 0 : `${list.length} - ${list.join(', ')}`;

export const asRelativeTime = (date) => {
  if (date) {
    const d = dayjs.utc(date, 'YYYY-MM-DD HH:mm:ss').local();
    return <span title={d.format('lll')}>{d.fromNow()}</span>;
  }
  return null;
};

// Converts milliseconds to a duration string (hhh:mm:ss.SSS).
export const asDuration = (milliseconds) => {
  if (milliseconds == null) return null;
  if (milliseconds === '*') return 'Multiple';
  const duration = dayjs.duration(Math.round(milliseconds), 'milliseconds');
  return `${Math.floor(duration.asHours())}${duration.format(':mm:ss')}${
    duration.milliseconds() ? duration.format('.SSS') : ''
  }`;
};

// Converts a duration string (hhh:mm:ss.SSS) to milliseconds.
export const asMilliseconds = (durationString) => {
  if (!durationString) return null;
  if (durationString === 'Multiple') return '*';
  // If the string is invalid, return NaN to fail validation.
  if (!durationString.match(/^(\d*\.)?\d+(:(\d*\.)?\d+){2}$/)) return NaN;
  const [hours, minutes, seconds] = durationString
    .split(':')
    .map((component) => parseFloat(component));
  const duration = dayjs.duration({ hours, minutes, seconds });
  return Math.ceil(duration.asMilliseconds());
};

// Converts milliseconds to hours.
export const millisAsHours = (milliseconds, digits = 2) => {
  if (milliseconds == null) return null;
  if (milliseconds === '*') return 'Multiple';
  const duration = dayjs.duration(Math.round(milliseconds), 'milliseconds');
  return asRoundedDecimal(duration.asHours(), { digits, minDigits: 0 });
};

export const asWholeHours = (milliseconds) => {
  if (milliseconds == null || ['', '*'].includes(milliseconds)) return null;
  const duration = dayjs.duration(milliseconds, 'milliseconds');
  // Take the whole hours
  const hours = Math.floor(duration.asHours());
  // Format as null if less than one hour
  return hours || null;
};

export const asRemainderMinutes = (milliseconds) => {
  if (milliseconds == null || ['', '*'].includes(milliseconds)) return null;
  const duration = dayjs.duration(milliseconds, 'milliseconds');
  // Take the whole remaining minutes that do not belong to a full hour
  const remainderMinutes = Math.floor(duration.asMinutes() % 60);
  // Format as null if less than one minute
  return remainderMinutes || null;
};

export const asRemainderSeconds = (milliseconds, digits = 3) => {
  if (milliseconds == null || ['', '*'].includes(milliseconds)) return null;
  const duration = dayjs.duration(milliseconds, 'milliseconds');
  // Take the remaining seconds that do not belong to a full minute
  const remainderSeconds = duration.asSeconds() % 60;
  // Format as null only if hours and minutes are non-zero
  if (milliseconds > 0 && !remainderSeconds) return null;
  return asRoundedDecimal(remainderSeconds, {
    digits,
    minDigits: 0,
  });
};

export const hmsToMilliseconds = ({ hours, minutes, seconds }) => {
  if (hours == null && minutes == null && seconds == null) {
    return null;
  }
  const duration = dayjs.duration({
    hours: hours || 0,
    minutes: minutes || 0,
    seconds: seconds || 0,
  });
  return duration.asMilliseconds();
};

export const asWarning = (valid) =>
  valid ? null : (
    <IconSpan>
      <WarningIcon className="text-danger" />
    </IconSpan>
  );

export const asCheck = (valid) =>
  valid ? (
    <IconSpan>
      <CheckIcon />
    </IconSpan>
  ) : null;

export const asTruncatedString = (string, maxLength) => {
  if (string == null) return null;
  return string.length <= maxLength
    ? string
    : `${string.substring(0, maxLength - 3)}...`;
};

export const TruncatedTextContent = styled.div``;

export const asTruncatedText = (content) => (
  <TruncatedTextContent className="text-truncate">
    <span title={content}>{content}</span>
  </TruncatedTextContent>
);

export const StrictTruncatedTextContent = styled.div``;

export const asStrictTruncatedText = (content) => (
  <StrictTruncatedTextContent className="text-truncate">
    <span title={content}>{content}</span>
  </StrictTruncatedTextContent>
);

// year index will be either 1, MULTI_YEAR, or ANNUAL_MULTI_YEAR
// allYears should be an array of years that yearIndex indexes into
export const asYearString = (yearIndex, years, allYears) => {
  if (yearIndex === MULTI_YEAR) {
    return `${asQuantity(years, {
      unit: 'years',
      singular: 'year',
    })} cumulative`;
  }
  if (yearIndex === ANNUAL_MULTI_YEAR) {
    return `${asQuantity(years, { unit: 'years', singular: 'year' })} annual`;
  }
  return allYears[yearIndex];
};

export const asCadence = (cadence) =>
  cadence === 1 ? 'Annually' : `Every ${cadence} years`;
